import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CMSImportStatusBadge } from '@/components/cms-import/CMSImportStatusBadge';
import { CMSImportUploadDialog } from '@/components/cms-import/CMSImportUploadDialog';
import { CMSSyncDialog } from '@/components/cms-import/CMSSyncDialog';
import { DataTable, TableColumn } from '@/components/common/DataTable';
import { usePagination } from '@/hooks/usePagination';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';
import { firmsApiClient } from '@/services/api/firmsService';
import type { CmsImportSchema } from '@/services/api/generated/cmsImportsService';
import { selectUser } from '@/state/selectors/root-selectors';
import { getResults, getTotalCount, toQueryParams } from '@/utils/pagination';

const getCmsTypeLabel = (cmsType: string): string => {
  const labels: Record<string, string> = {
    casepeer: 'CasePeer',
    clio: 'Clio',
    filevine: 'Filevine',
    mycase: 'MyCase',
    neos: 'Neos',
    litify: 'Litify',
    smartadvocate: 'SmartAdvocate',
  };
  return labels[cmsType] || cmsType;
};

export const CMSImportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);
  const [showSync, setShowSync] = useState(false);

  const currentUser = useSelector(selectUser);

  const { state, actions } = usePagination({
    defaultLimit: 50,
    defaultSort: '-created_at',
  });

  const queryParams = useMemo(
    () => toQueryParams(state),
    [state],
  );

  const { data, isLoading } = cmsImportsApiClient.useCmsImportListQuery(queryParams);
  const imports = useMemo(() => getResults(data), [data]);
  const totalCount = getTotalCount(data);

  const { data: firmData } = firmsApiClient.useGetFirmQuery(
    currentUser?.firm ?? '',
    {
      skip: !currentUser?.firm,
    },
  );

  // Show CMS sync button if user's firm uses a supported CMS
  const firmCmsSystem = firmData?.cms_system;
  const hasSupportedCmsIntegration =
    firmCmsSystem === 'CASEpeer' ||
    firmCmsSystem === 'Clio Manage' ||
    firmCmsSystem === 'Filevine' ||
    firmCmsSystem === 'Neos' ||
    firmCmsSystem === 'MyCase' ||
    currentUser?.is_staff;

  const columns: TableColumn<CmsImportSchema>[] = useMemo(
    () => [
      {
        id: 'cms_type',
        header: 'Source',
        accessorKey: 'cms_type',
        width: '120px',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (row) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
            {getCmsTypeLabel(row.cms_type)}
          </span>
        ),
      },
      {
        id: 'firm',
        header: 'Firm',
        accessorKey: 'firm_name',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (row) => (
          <span
            className="truncate max-w-[250px] inline-block font-medium text-zinc-900 dark:text-white"
            title={row.firm_name}
          >
            {row.firm_name}
          </span>
        ),
      },
      {
        id: 'phase',
        header: 'Status',
        accessorKey: 'phase',
        width: '130px',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (row) => (
          <CMSImportStatusBadge
            phase={row.error_message ? 'failed' : row.phase}
          />
        ),
      },
      {
        id: 'created_by',
        header: 'Created By',
        accessorKey: 'created_by_name',
        width: '180px',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (row) => (
          <span
            className="truncate max-w-[160px] inline-block text-zinc-600 dark:text-zinc-400"
            title={row.created_by_name ?? undefined}
          >
            {row.created_by_name}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        accessorKey: 'created_at',
        width: '180px',
        sortable: true,
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (_row, value) => {
          const date = new Date(value as string);
          return (
            <div className="text-sm text-zinc-500 dark:text-zinc-400">
              {date.toLocaleDateString()}{' '}
              {date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-6 px-10 py-8 h-full">
      <DataTable
        data={imports}
        columns={columns}
        title="CMS Task Import"
        description="Import tasks from external CMS systems like CasePeer into Finch"
        searchEnabled={false}
        disableClientFilter
        actions={[
          ...(hasSupportedCmsIntegration
            ? [
                {
                  label: 'Sync from CMS',
                  onClick: () => setShowSync(true),
                  color: 'blue' as const,
                },
              ]
            : []),
          {
            label: 'New Import',
            onClick: () => setShowUpload(true),
            color: 'zinc' as const,
          },
        ]}
        paginationEnabled
        currentPage={actions.getCurrentPage()}
        totalPages={actions.getTotalPages(totalCount)}
        totalCount={totalCount}
        pageSize={state.limit}
        onPageChange={actions.setPage}
        onPageSizeChange={actions.setLimit}
        isLoading={isLoading}
        onRowClick={(cmsImport) =>
          navigate(`/profile/cms-import/${cmsImport.id}`)
        }
        emptyMessage="No imports yet. Click 'New Import' to import tasks from your CMS."
      />

      {showUpload && (
        <CMSImportUploadDialog
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
        />
      )}

      {showSync && (
        <CMSSyncDialog isOpen={showSync} onClose={() => setShowSync(false)} />
      )}
    </div>
  );
};
